import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, Accessibility, ShieldCheck, ArrowRight } from "lucide-react";

import Seo, { breadcrumb } from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal, FloatingBlob } from "@/components/motion";
import { CoveButton } from "@/components/CoveButton";
import { submitContact, submitNetlify } from "@/lib/api";
import { SITE, IMAGES } from "@/data/content";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const APPOINTMENT_TYPES = [
  "Initial craniosacral session",
  "Follow-up session",
  "Extended session",
  "Not sure yet, I have a question",
];

const initialForm = {
  name: "",
  email: "",
  phone: "",
  appointment_type: "",
  contact_method: "Email",
  message: "",
  consent: false,
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.appointment_type || !form.message) {
      toast.error("Please complete the required fields.");
      return;
    }
    if (!form.consent) {
      toast.error("Please tick the consent box so we can reply to you.");
      return;
    }
    setSubmitting(true);
    let ok = false;
    try {
      await submitContact(form);
      ok = true;
    } catch (err) {
      // backend unavailable (e.g. Netlify-only deploy), fall through to Netlify
    }
    try {
      const res = await submitNetlify(form);
      if (res && res.ok) ok = true;
    } catch (err) {
      // not on Netlify / offline, ignore
    }
    if (ok) {
      toast.success("Thank you, your message has reached Laura. You'll hear back personally.");
      setForm(initialForm);
    } else {
      toast.error("Something went wrong sending your message. Please try again or email directly.");
    }
    setSubmitting(false);
  };

  const inputCls =
    "bg-white/60 border-cove-slate/20 h-12 rounded-xl text-cove-slate placeholder:text-cove-taupe/70 focus-visible:ring-cove-slate";

  return (
    <>
      <Seo
        title="Contact & Booking"
        description="Request a craniosacral session or ask a question at Cranio Cove. Reach Laura McLelland by the contact form, email, or phone. Serving [City] and the surrounding area."
        path="/contact"
      />
      <PageHero
        overline="Contact & booking"
        titleLines={["Let's find a", "time to connect"]}
        intro="The quickest way to book is through the online calendar. Prefer to ask a question first? Send a message and Laura will reply personally."
        breadcrumb={breadcrumb([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }])}
        heroImage={{ src: IMAGES.coveGolden, alt: "A warm golden sunset over a still lake with soft reflections" }}
      />

      {/* Primary Calendly CTA, the most prominent path */}
      <section className="px-6 md:px-12 -mt-2 md:-mt-4 mb-10 md:mb-14" data-testid="contact-booking-cta">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <div className="rounded-[2rem] md:rounded-[3rem] bg-cove-nightblue text-cove-cream grain px-8 md:px-14 py-10 md:py-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="max-w-xl">
                <p className="text-xs tracking-[0.25em] uppercase text-cove-blush mb-3">Book directly</p>
                <h2 className="font-serif text-3xl md:text-4xl leading-tight">
                  Reserve your session in a couple of taps.
                </h2>
                <p className="mt-3 text-cove-blue leading-relaxed">
                  Choose a 1-hour or 30-minute session and pick the time that works best for you.
                </p>
              </div>
              <div className="shrink-0">
                <CoveButton calendly variant="light" testId="contact-primary-book" className="px-8 py-4 text-base">
                  Book with Calendly
                </CoveButton>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative px-6 md:px-12 pb-24 md:pb-32 overflow-hidden" data-testid="contact-section">
        <FloatingBlob color="sage" size={360} className="top-10 -left-24" delay={0.2} />
        <FloatingBlob color="blush" size={320} className="bottom-20 -right-20" delay={0.5} drift={30} />
        <div className="relative mx-auto max-w-[1400px]">
          <Reveal>
            <div className="mb-10 md:mb-14 max-w-2xl">
              <p className="text-xs tracking-[0.25em] uppercase text-cove-rose mb-3">Or, send a message</p>
              <h2 className="font-serif text-3xl md:text-4xl leading-tight text-cove-slate">
                Have a question first?
              </h2>
              <p className="mt-3 text-cove-olive leading-relaxed">
                Share a little about what you're hoping for and Laura will reply personally to arrange a time.
              </p>
            </div>
          </Reveal>
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-20">
          {/* Form */}
          <Reveal>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form" name="contact" method="POST" data-netlify="true" noValidate>
              <input type="hidden" name="form-name" value="contact" />
              <p className="hidden">
                <label>Don’t fill this out if you’re human: <input name="bot-field" tabIndex={-1} autoComplete="off" /></label>
              </p>
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-cove-olive">Name <span className="text-cove-rose">*</span></Label>
                  <Input id="name" value={form.name} onChange={(e) => update("name", e.target.value)} className={inputCls} required data-testid="contact-name" autoComplete="name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-cove-olive">Email <span className="text-cove-rose">*</span></Label>
                  <Input id="email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} required data-testid="contact-email" autoComplete="email" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-cove-olive">Phone <span className="text-cove-taupe/70">(optional)</span></Label>
                  <Input id="phone" type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputCls} data-testid="contact-phone" autoComplete="tel" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="appointment_type" className="text-cove-olive">Preferred appointment type <span className="text-cove-rose">*</span></Label>
                  <Select value={form.appointment_type} onValueChange={(v) => update("appointment_type", v)}>
                    <SelectTrigger id="appointment_type" className={`${inputCls} data-[placeholder]:text-cove-taupe/70`} data-testid="contact-appointment-type">
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent className="bg-cove-cream border-cove-slate/20">
                      {APPOINTMENT_TYPES.map((t) => (
                        <SelectItem key={t} value={t} data-testid={`appointment-option-${t.slice(0, 8)}`}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <fieldset className="space-y-3">
                <legend className="text-sm text-cove-olive mb-1">Preferred contact method</legend>
                <RadioGroup
                  value={form.contact_method}
                  onValueChange={(v) => update("contact_method", v)}
                  className="flex gap-6"
                  data-testid="contact-method"
                >
                  {["Email", "Phone"].map((m) => (
                    <div key={m} className="flex items-center gap-2">
                      <RadioGroupItem value={m} id={`method-${m}`} className="border-cove-slate/40 text-cove-slate" />
                      <Label htmlFor={`method-${m}`} className="text-cove-slate cursor-pointer">{m}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </fieldset>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-cove-olive">Message <span className="text-cove-rose">*</span></Label>
                <Textarea id="message" rows={5} value={form.message} onChange={(e) => update("message", e.target.value)} className={`${inputCls} h-auto py-3`} required data-testid="contact-message" placeholder="A little about what you're hoping for, and any preferred days or times." />
              </div>

              <div className="flex items-start gap-3">
                <Checkbox id="consent" checked={form.consent} onCheckedChange={(v) => update("consent", Boolean(v))} className="mt-1 border-cove-slate/40" data-testid="contact-consent" />
                <Label htmlFor="consent" className="text-sm leading-relaxed text-cove-olive font-normal cursor-pointer">
                  I consent to Cranio Cove storing the details I've provided in order to respond to
                  my enquiry. <span className="text-cove-rose">*</span>
                </Label>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-cove-slate px-8 py-4 text-sm font-medium tracking-wide text-cove-cream transition-colors duration-300 hover:bg-cove-olive disabled:opacity-60"
              >
                {submitting ? "Sending..." : "Send Message"} <ArrowRight size={16} />
              </button>

              <p className="flex items-start gap-2 text-xs text-cove-taupe leading-relaxed pt-2">
                <ShieldCheck size={16} className="mt-0.5 shrink-0 text-cove-sage" />
                Your information is kept private and is used only to respond to your enquiry. Please
                don't share sensitive medical details through this form.
              </p>
            </form>
          </Reveal>

          {/* Info */}
          <Reveal delay={0.1}>
            <div className="rounded-[2rem] border border-cove-slate/15 bg-cove-blush/25 overflow-hidden">
              <div className="h-48 md:h-56 overflow-hidden">
                <img src={IMAGES.coveTurquoise} alt="A quiet turquoise cove framed by pine trees and rocky cliffs" loading="lazy" className="w-full h-full object-cover" />
              </div>
              <div className="p-8 md:p-10 space-y-8">
              <div>
                <h2 className="font-serif text-3xl text-cove-slate">Get in touch directly</h2>
                <ul className="mt-6 space-y-4 text-cove-olive">
                  <li className="flex items-center gap-3">
                    <Mail size={18} className="text-cove-rose shrink-0" />
                    <a href={`mailto:${SITE.email}`} className="hover:text-cove-slate transition-colors" data-testid="contact-email-link">{SITE.email}</a>
                  </li>
                  <li className="flex items-center gap-3">
                    <Phone size={18} className="text-cove-rose shrink-0" />
                    <span>{SITE.phone}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <MapPin size={18} className="text-cove-rose shrink-0 mt-0.5" />
                    <span>
                      Cranio Cove is located within{" "}
                      <a
                        href={SITE.hearthUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline decoration-cove-rose/40 underline-offset-2 hover:text-cove-slate transition-colors"
                        data-testid="contact-hearth-link"
                      >
                        {SITE.hearthName}
                      </a>
                      <br />
                      200 S. Broadway
                      <br />
                      Building 1
                      <br />
                      Suite 100
                      <br />
                      Tarrytown, NY 10591
                      <br />
                      <a
                        href={SITE.hearthUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm underline decoration-cove-rose/40 underline-offset-2 hover:text-cove-slate transition-colors"
                        data-testid="contact-hearth-url"
                      >
                        www.wellwombn.com
                      </a>
                      <br />
                      <span className="text-sm text-cove-taupe">Serving {SITE.serviceArea}.</span>
                    </span>
                  </li>
                </ul>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <CoveButton calendly variant="primary" testId="contact-calendly-button" className="px-6 py-3">Book via Calendar</CoveButton>
                  <a
                    href={SITE.googleReviewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-google-review"
                    className="inline-flex items-center justify-center rounded-full border border-cove-slate/40 px-6 py-3 text-sm font-medium text-cove-slate hover:bg-cove-slate/[0.04] transition-colors"
                  >
                    Leave a Google review
                  </a>
                </div>
              </div>

              <div className="pt-6 border-t border-cove-slate/15">
                <h3 className="flex items-center gap-2 font-serif text-2xl text-cove-slate">
                  <Clock size={18} className="text-cove-rose" /> Hours
                </h3>
                <ul className="mt-4 space-y-2 text-sm text-cove-olive">
                  {SITE.hours.map((h) => (
                    <li key={h.day} className="flex justify-between gap-4">
                      <span>{h.day}</span>
                      <span className="text-cove-slate">{h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="pt-6 border-t border-cove-slate/15 grid gap-5">
                <div className="flex items-start gap-3">
                  <Accessibility size={18} className="text-cove-rose shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-serif text-xl text-cove-slate">Accessibility</h3>
                    <p className="mt-1 text-sm text-cove-olive leading-relaxed">Please get in touch about any access needs, Laura will gladly talk through arrangements to help your visit feel comfortable.</p>
                  </div>
                </div>
              </div>
              </div>
            </div>
          </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
